import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useApi } from './api-context';

// Catálogos de calidad de la empresa activa (tipos de documento, procesos y normas),
// compartidos por Estudio de tiempos (so=420) y la Lista Maestra (so=430). Requieren
// `quality:catalogs:read`; la empresa activa va en la clave y cada respuesta pasa por Zod.

const Q = '/api/v1/quality';
const envelope = <T extends z.ZodType>(data: T) => z.object({ success: z.literal(true), data });

export const catalogResponseSchema = envelope(
  z.array(z.object({ id: z.string(), name: z.string(), isActive: z.boolean() })),
);
export type CatalogItem = z.infer<typeof catalogResponseSchema>['data'][number];

const normCatalogResponseSchema = envelope(
  z.array(
    z.object({
      id: z.string(),
      normCode: z.string(),
      clause: z.string(),
      title: z.string(),
      isActive: z.boolean(),
    }),
  ),
);
export type NormClause = z.infer<typeof normCatalogResponseSchema>['data'][number];

export interface CatalogScope {
  companyId: string | null;
  /** Permiso de catálogos concedido. */
  enabled: boolean;
}

function useParsed<T>(
  name: string,
  parse: (raw: unknown) => T,
  { companyId, enabled }: CatalogScope,
): UseQueryResult<T> {
  const api = useApi();
  const url = `${Q}/${name}`;
  return useQuery({
    queryKey: ['quality-catalogs', companyId, name],
    queryFn: async () => parse(await api.get<unknown>(url)),
    enabled: enabled && companyId !== null,
    staleTime: 5 * 60_000,
  });
}

export function useCatalog(
  catalog: 'document-types' | 'process-areas',
  scope: CatalogScope,
): UseQueryResult<CatalogItem[]> {
  return useParsed(catalog, (r) => catalogResponseSchema.parse(r).data, scope);
}

export function useNormCatalog(scope: CatalogScope): UseQueryResult<NormClause[]> {
  return useParsed('norm-catalog', (r) => normCatalogResponseSchema.parse(r).data, scope);
}

const NATURAL = new Intl.Collator('es', { numeric: true });

export interface NormOptions {
  /** Incluir también lo desactivado: para filtrar lo ya clasificado (auditoría), nunca
   *  para clasificar de nuevo. */
  includeInactive?: boolean;
}

/** Normas con al menos una cláusula activa (o cualquiera, si se piden también las
 *  desactivadas), sin repetir y en orden. */
export function normCodesOf(norms: readonly NormClause[], opts: NormOptions = {}): string[] {
  const usable = opts.includeInactive === true ? norms : norms.filter((n) => n.isActive);
  return [...new Set(usable.map((n) => n.normCode))].sort((a, b) => NATURAL.compare(a, b));
}

/** Cláusulas de una norma en orden natural ("4.1" antes que "10.2"): solo las activas,
 *  salvo que se pidan también las desactivadas. */
export function clausesOf(
  norms: readonly NormClause[],
  normCode: string,
  opts: NormOptions = {},
): NormClause[] {
  return norms
    .filter((n) => (opts.includeInactive === true || n.isActive) && n.normCode === normCode)
    .sort((a, b) => NATURAL.compare(a.clause, b.clause));
}
